import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Home as HomeIcon, MessageSquare, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [myProperties, setMyProperties] = useState([]);
  const [myInquiries, setMyInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [propertiesRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/properties/user/my-properties`, { withCredentials: true }),
        axios.get(`${API}/inquiries/my-inquiries`, { withCredentials: true })
      ]);
      setMyProperties(propertiesRes.data);
      setMyInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="dashboard-heading">
              My Dashboard
            </h1>
            <p className="text-lg text-slate-600">Manage your properties and inquiries</p>
          </div>
          <Button
            data-testid="add-property-button"
            onClick={() => navigate('/add-property')}
            className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Property
          </Button>
        </div>

        <Tabs defaultValue="properties" className="w-full" data-testid="dashboard-tabs">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
            <TabsTrigger value="properties" data-testid="tab-properties">
              <HomeIcon className="w-4 h-4 mr-2" />
              My Properties
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              <MessageSquare className="w-4 h-4 mr-2" />
              My Inquiries
            </TabsTrigger>
          </TabsList>

          <TabsContent value="properties" data-testid="properties-tab-content">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600">Loading properties...</p>
              </div>
            ) : myProperties.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-slate-100">
                <HomeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-4">No properties listed yet</p>
                <Button
                  data-testid="add-first-property"
                  onClick={() => navigate('/add-property')}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  Add Your First Property
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myProperties.map((property) => (
                  <div
                    key={property.property_id}
                    data-testid={`my-property-${property.property_id}`}
                    className="bg-white rounded-lg overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/properties/${property.property_id}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={property.images?.[0] || 'https://images.unsplash.com/photo-1570709073200-5c34c5fa8688'}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-primary text-white text-sm font-medium rounded-md">
                          {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 text-white text-sm font-medium rounded-md ${
                          property.status === 'available' ? 'bg-green-500' : 'bg-slate-500'
                        }`}>
                          {property.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-medium mb-2 line-clamp-1">{property.title}</h3>
                      <div className="flex items-center text-slate-600 mb-3">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.location?.district}, Kerala</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-semibold text-primary">
                          ₹{property.price?.toLocaleString('en-IN')}
                        </span>
                        <div className="flex gap-3 text-sm text-slate-600">
                          {property.bedrooms && <span>{property.bedrooms} BHK</span>}
                          {property.area && <span>{property.area} sq.ft</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="inquiries" data-testid="inquiries-tab-content">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600">Loading inquiries...</p>
              </div>
            ) : myInquiries.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-slate-100">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg">No inquiries yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myInquiries.map((inquiry) => (
                  <div
                    key={inquiry.inquiry_id}
                    data-testid={`inquiry-${inquiry.inquiry_id}`}
                    className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-medium text-lg">Property Inquiry</h3>
                        <p className="text-sm text-slate-600">Property ID: {inquiry.property_id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-md text-sm font-medium ${
                        inquiry.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {inquiry.status}
                      </span>
                    </div>
                    <p className="text-slate-700 mb-4">{inquiry.message}</p>
                    <div className="text-sm text-slate-600">
                      <p>Contact: {inquiry.user_email}</p>
                      {inquiry.user_phone && <p>Phone: {inquiry.user_phone}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}